import type { User } from 'oidc-client-ts';
import { trackAuthError, trackLoggedOut } from './authTracking';

// One shared silent renewal for every 401 handler.
// Concurrent callers wait on a single signinSilent(). A failed renewal logs out
// only when storage still holds the same session. Timeouts, dropped connections
// and server errors keep that session so a blip does not log the user out; any
// other answer from the token endpoint means the session cannot be renewed.

type OidcClient = {
  getUser: () => Promise<User | null>;
  signinSilent: () => Promise<User | null>;
  removeUser: () => Promise<void>;
  signinRedirect: () => Promise<void>;
};

const AUTH_FLOW_ROUTE_NAMES = ['login', 'silent-refresh', 'logout'];

const TRANSIENT_ERROR_NAMES = new Set(['ErrorTimeout', 'AbortError']);
const TRANSIENT_OAUTH_ERRORS = new Set(['server_error', 'temporarily_unavailable']);

// oidc-client-ts reports a token endpoint answer it cannot parse as a plain
// Error: "<statusText> (<status>)" or "Invalid response Content-Type: ...".
const HTTP_STATUS_IN_MESSAGE = /\((\d{3})\)/;
const NON_JSON_RESPONSE = /^Invalid response Content-Type/;

type RenewTarget = {
  name?: string | null;
  fullPath?: string;
};

export type RenewResult =
  | { outcome: 'renewed'; user: User }
  | { outcome: 'recovered'; user: User }
  | { outcome: 'transient_failure'; user: User | null }
  | { outcome: 'logged_out' };

let inFlight: Promise<RenewResult> | null = null;
let loggingOut = false;
let pendingTarget: RenewTarget | undefined;

export function isRenewInFlight() {
  return inFlight !== null;
}

export function renewSession(target?: RenewTarget) {
  if (target?.fullPath) pendingTarget = target;
  if (inFlight) return inFlight;

  inFlight = performRenew().finally(() => {
    inFlight = null;
    pendingTarget = undefined;
  });
  return inFlight;
}

async function performRenew(): Promise<RenewResult> {
  const oidcClient = useOidcClient() as OidcClient;
  let started: User | null = null;

  try {
    started = await oidcClient.getUser();
    const resolved = await oidcClient.signinSilent();
    if (resolved) {
      useAuth().state.value.user = resolved;
      return { outcome: 'renewed', user: resolved };
    }
  } catch (error) {
    return settleFailure(oidcClient, started, error);
  }

  return settleMissingUser(oidcClient, started);
}

function isTransientFailure(error: unknown) {
  if (error instanceof TypeError) return true;

  const err = error as { name?: unknown; message?: unknown; error?: unknown } | null;
  if (typeof err?.name === 'string' && TRANSIENT_ERROR_NAMES.has(err.name)) return true;
  if (typeof err?.error === 'string') return TRANSIENT_OAUTH_ERRORS.has(err.error);

  const message = typeof err?.message === 'string' ? err.message : '';
  if (NON_JSON_RESPONSE.test(message)) return true;
  return Number(message.match(HTTP_STATUS_IN_MESSAGE)?.[1]) >= 500;
}

async function settleFailure(oidcClient: OidcClient, started: User | null, error: unknown): Promise<RenewResult> {
  const recovered = await recoveredSession(oidcClient, started);
  if (recovered) return { outcome: 'recovered', user: recovered };
  if (isTransientFailure(error)) return transientFailure(oidcClient, started, error);

  await logoutOnce(oidcClient, error);
  return { outcome: 'logged_out' };
}

async function settleMissingUser(oidcClient: OidcClient, started: User | null): Promise<RenewResult> {
  const recovered = await recoveredSession(oidcClient, started);
  if (recovered) return { outcome: 'recovered', user: recovered };

  const current = await readStored(oidcClient);
  if (current) {
    return transientFailure(oidcClient, current, new Error('silent renew returned no user'));
  }

  await logoutOnce(oidcClient, new Error('silent renew returned no user'));
  return { outcome: 'logged_out' };
}

async function transientFailure(oidcClient: OidcClient, fallback: User | null, error: unknown) {
  await trackAuthError('silent_renew', error, undefined, { renew_outcome: 'transient_failure' });
  const user = (await readStored(oidcClient)) ?? fallback;
  return { outcome: 'transient_failure' as const, user };
}

function sessionWasReplaced(started: User | null, current: User | null) {
  if (!current) return false;
  if (current.refresh_token && current.refresh_token !== started?.refresh_token) return true;
  if (current.id_token && current.id_token !== started?.id_token) return true;
  if (current.expires_at && current.expires_at !== started?.expires_at) return true;
  return false;
}

async function readStored(oidcClient: OidcClient) {
  try {
    return await oidcClient.getUser();
  } catch {
    return null;
  }
}

async function recoveredSession(oidcClient: OidcClient, started: User | null) {
  const current = await readStored(oidcClient);
  if (!current || !sessionWasReplaced(started, current)) return null;

  useAuth().state.value.user = current;
  return current;
}

async function logoutOnce(oidcClient: OidcClient, error: unknown) {
  if (loggingOut) return;
  loggingOut = true;
  try {
    await rememberWhereToReturn().catch(() => undefined);
    await oidcClient.removeUser();
    await Promise.all([
      trackAuthError('silent_renew', error, undefined, { renew_outcome: 'logged_out' }),
      trackLoggedOut('token_invalid'),
    ]);
    // signinRedirect assigns location and does not resolve. Awaiting it keeps
    // this renewal in flight until the page unloads, so a second logout cannot start.
    await oidcClient.signinRedirect().catch(() => undefined);
  } finally {
    loggingOut = false;
  }
}

async function rememberWhereToReturn() {
  // A later navigation can join a renewal that started with no target.
  // Keep that destination; useRoute() inside middleware is still the page being left.
  const route = pendingTarget?.fullPath ? pendingTarget : useRoute();
  const name = typeof route.name === 'string' ? route.name : '';
  if (AUTH_FLOW_ROUTE_NAMES.includes(name)) return;

  if (route.fullPath) useRedirectPath().value = route.fullPath;
  await useIntercom().shutdown();
}
